import { create } from "zustand";
import {
  getMyAlerts,
  getUnreadAlertCount,
  markAlertAsRead,
  dismissAlert,
  markAllAlertsAsRead,
  Alert,
  Pagination
} from "@/services/DashboardAnalyticsService";
import { registerStoreReset } from "@/store/storeResetUtils";

// Re-export Alert type as Notification for semantic clarity
export type Notification = Alert;

export interface NotificationFilters {
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  alertType?: string;
  isRead?: boolean;
  includeDismissed: boolean;
}

interface NotificationStore {
  // State
  notifications: Notification[];
  unreadCount: number;
  hasNewNotification: boolean;
  pagination: Pagination | null;
  loading: boolean;
  error: string | null;
  panelOpen: boolean;
  filters: NotificationFilters;

  // Actions
  fetchNotifications: (page?: number, limit?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: number) => Promise<boolean>;
  markAllAsRead: () => Promise<boolean>;
  dismissNotification: (id: number) => Promise<boolean>;
  setPanelOpen: (open: boolean) => void;
  setFilters: (filters: Partial<NotificationFilters>) => void;
  resetFilters: () => void;
  resetError: () => void;
  resetStore: () => void;
  clearNewNotificationFlag: () => void;
}

const initialFilters: NotificationFilters = {
  includeDismissed: false
};

const useNotificationStore = create<NotificationStore>((set, get) => ({
  // Initial state
  notifications: [],
  unreadCount: 0,
  hasNewNotification: false,
  pagination: null,
  loading: false,
  error: null,
  panelOpen: false,
  filters: initialFilters,

  // Fetch notifications with current filters
  fetchNotifications: async (page = 1, limit = 10) => {
    try {
      set({ loading: true, error: null });
      const { filters } = get();

      const response = await getMyAlerts(page, limit, filters.includeDismissed);

      if (response.success && response.data) {
        // Apply client-side filtering for priority and read status if needed
        let filteredData = response.data;

        if (filters.priority) {
          filteredData = filteredData.filter(n => n.priority === filters.priority);
        }
        if (filters.alertType) {
          filteredData = filteredData.filter(n => n.alert_type === filters.alertType);
        }
        if (filters.isRead !== undefined) {
          filteredData = filteredData.filter(n => n.is_read === filters.isRead);
        }

        set({
          notifications: filteredData,
          pagination: response.pagination || null,
          loading: false
        });
      } else {
        const errorMsg = typeof response.error === 'string'
          ? response.error
          : response.error?.message || 'Failed to fetch notifications';
        set({
          error: errorMsg,
          loading: false
        });
      }
    } catch (error: any) {
      console.error("Fetch Notifications Error:", error);
      set({ error: error.message || 'An error occurred', loading: false });
    }
  },

  // Fetch unread count (lightweight call for badge)
  fetchUnreadCount: async () => {
    try {
      const previousCount = get().unreadCount;
      const response = await getUnreadAlertCount();
      if (response.success && response.data) {
        const newCount = response.data.count;
        // Detect if new notifications arrived (count increased and we had a previous count)
        const hasNew = newCount > previousCount && previousCount >= 0;
        set({
          unreadCount: newCount,
          hasNewNotification: hasNew
        });
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },

  // Mark single notification as read
  markAsRead: async (id: number) => {
    try {
      // Optimistic update
      const notification = get().notifications.find(n => n.id === id);
      const wasUnread = notification && !notification.is_read;

      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        ),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount
      }));

      const response = await markAlertAsRead(id);

      if (!response.success) {
        // Rollback on failure
        set(state => ({
          notifications: state.notifications.map(n =>
            n.id === id ? { ...n, is_read: false, read_at: undefined } : n
          ),
          unreadCount: wasUnread ? state.unreadCount + 1 : state.unreadCount,
          error: response.error?.message || 'Failed to mark as read'
        }));
        return false;
      }
      return true;
    } catch (error: any) {
      console.error("Mark As Read Error:", error);
      set({ error: error.message });
      return false;
    }
  },

  // Mark all as read
  markAllAsRead: async () => {
    try {
      // Optimistic update
      const previousNotifications = get().notifications;
      const previousUnreadCount = get().unreadCount;

      set(state => ({
        notifications: state.notifications.map(n => ({
          ...n,
          is_read: true,
          read_at: n.is_read ? n.read_at : new Date().toISOString()
        })),
        unreadCount: 0
      }));

      const response = await markAllAlertsAsRead();

      if (!response.success) {
        // Rollback on failure
        set({
          notifications: previousNotifications,
          unreadCount: previousUnreadCount,
          error: response.error?.message || 'Failed to mark all as read'
        });
        return false;
      }
      return true;
    } catch (error: any) {
      console.error("Mark All As Read Error:", error);
      set({ error: error.message });
      return false;
    }
  },

  // Dismiss notification
  dismissNotification: async (id: number) => {
    try {
      // Optimistic update
      const notification = get().notifications.find(n => n.id === id);
      const wasUnread = notification && !notification.is_read;
      const previousNotifications = get().notifications;
      const previousUnreadCount = get().unreadCount;

      set(state => ({
        notifications: state.notifications.filter(n => n.id !== id),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount
      }));

      const response = await dismissAlert(id);

      if (!response.success) {
        // Rollback on failure
        set({
          notifications: previousNotifications,
          unreadCount: previousUnreadCount,
          error: response.error?.message || 'Failed to dismiss notification'
        });
        return false;
      }
      return true;
    } catch (error: any) {
      console.error("Dismiss Notification Error:", error);
      set({ error: error.message });
      return false;
    }
  },

  // UI actions
  setPanelOpen: (open: boolean) => set({ panelOpen: open }),

  setFilters: (newFilters: Partial<NotificationFilters>) =>
    set(state => ({
      filters: { ...state.filters, ...newFilters }
    })),

  resetFilters: () => set({ filters: initialFilters }),

  resetError: () => set({ error: null }),

  // Clear new notification flag (call after playing sound)
  clearNewNotificationFlag: () => set({ hasNewNotification: false }),

  // Reset store to initial state
  resetStore: () => set({
    notifications: [],
    unreadCount: 0,
    hasNewNotification: false,
    pagination: null,
    loading: false,
    error: null,
    panelOpen: false,
    filters: initialFilters
  })
}));

// Register this store for centralized reset on logout
registerStoreReset('notifications', () => useNotificationStore.getState().resetStore());

export default useNotificationStore;
